import {
  Backdrop,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Modal,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "app/hooks";
import HolzfinderDateInput from "components/inputs/HolzfinderDateInput";
import HolzfinderRadio from "components/inputs/HolzfinderRadio";
import { bulkEditStatus } from "features/lotlist/bulkEditStatusSlice";
import { selectSorting } from "features/lotlist/filterAndSortSlice";
import { getLotList } from "features/lotlist/lotListSlice";
import {
  selectLotListTab,
  selectPageLotList,
  selectRowsPerPageLotList,
} from "features/table/tableSlice";
import { selectUserRole } from "features/user/userSlice";
import { LotOfferStatus } from "interfaces/LotOfferStatus";
import { DateTime } from "luxon";
import React, { useContext, useState } from "react";
import { lotListTabNames, ModalContext } from "../LosListe";

interface BulkStatusEditModalProps {
  checkedBoxes: string[];
  searchQuery?: string;
}

const BulkStatusEdit: React.FC<BulkStatusEditModalProps> = (props) => {
  const { checkedBoxes, searchQuery } = props;
  const dispatch = useAppDispatch();
  const userRole = useAppSelector(selectUserRole);
  const rowsPerPage = useAppSelector(selectRowsPerPageLotList);
  const page = useAppSelector(selectPageLotList);
  const tab = useAppSelector(selectLotListTab);
  const sort = useAppSelector(selectSorting);
  const {
    modalOpened,
    handleClose,
    errorOpened,
    handleErrorMessage,
    errorMessage,
  } = useContext(ModalContext);
  const [lotStatus, setLotStatus] = useState<LotOfferStatus>(
    LotOfferStatus.INACTIVE
  );
  const [lotPublishedAt, setLotPublishedAt] = useState<string>(
    DateTime.now().toISO()
  );
  const handlePlannedDate = (date: string) => {
    setLotPublishedAt(date);
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    switch (event.target.value) {
      case LotOfferStatus.ACTIVE:
        setLotStatus(LotOfferStatus.ACTIVE);
        break;
      case LotOfferStatus.INACTIVE:
        setLotStatus(LotOfferStatus.INACTIVE);
        break;
      case LotOfferStatus.PLANNED:
        setLotStatus(LotOfferStatus.PLANNED);
        break;
      default:
        setLotStatus(LotOfferStatus.INACTIVE);
        break;
    }
  };
  const handleDelete = () => {
    dispatch(
      bulkEditStatus({
        ids: checkedBoxes,
        status: lotStatus,
        published_at: lotPublishedAt,
        endpoint: userRole,
      })
    )
      .then((result: any) => {
        const { data } = result.payload;

        dispatch(
          getLotList({
            items_per_page: rowsPerPage,
            page: page + 1,
            search: searchQuery,
            status: lotListTabNames[tab],
            sort: sort,
          })
        );
        if (data.data && data.data.errors && data.data.errors.ids) {
          handleErrorMessage(data.data.errors.ids.join(", "));
        } else {
          handleErrorMessage("");
          handleClose(true);
        }
      })
      .catch((e) => {
        console.error(e);
      });
  };
  return (
    <Backdrop sx={{ zIndex: 1200 }} open={modalOpened || !!errorOpened}>
      <Modal
        hideBackdrop
        open={modalOpened && !errorOpened}
        onClose={() => handleClose()}
        aria-labelledby="modal-title"
      >
        <Box
          sx={{
            width: "30%",
            minWidth: "300px",
            maxWidth: "90%",
            position: "absolute",
            top: "50%",
            left: "50%",
            backgroundColor: "white",
            transform: "translate(-50%, -50%)",
            borderRadius: "16px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            padding: 4,
            textAlign: "center",
          }}
        >
          <Typography fontSize={20} fontWeight={"bold"}>
            {`Wählen Sie die gewünschte Aktion für die markierten Lose (${checkedBoxes.length}) aus.`}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              alignContent: "center",
            }}
          >
            <Box sx={{ flexDirection: "row" }}>
              <FormControl>
                <RadioGroup value={lotStatus} onChange={handleChange}>
                  <FormControlLabel
                    value={LotOfferStatus.ACTIVE}
                    control={<HolzfinderRadio />}
                    label="Aktiv"
                  />
                  <FormControlLabel
                    value={LotOfferStatus.INACTIVE}
                    control={<HolzfinderRadio />}
                    label="Inaktiv"
                  />
                  <FormControlLabel
                    value={LotOfferStatus.PLANNED}
                    control={<HolzfinderRadio />}
                    label="Geplant"
                  />
                </RadioGroup>
                <HolzfinderDateInput
                  value={lotPublishedAt}
                  onChange={handlePlannedDate}
                  disablePast={true}
                  disabled={lotStatus !== LotOfferStatus.PLANNED}
                />
              </FormControl>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 2,
              justifyContent: "space-between",
            }}
          >
            <Button
              onClick={() => {
                handleClose();
              }}
              sx={{
                flexGrow: 1,
                borderRadius: "26px",
                color: "#072D26",
                fontWeight: "bold",
                border: "2px solid #C0D904",
                height: "51px",
                paddingLeft: "50px",
                paddingRight: "50px",
                "&:hover": { background: "#c0d904" },
              }}
            >
              Abbrechen
            </Button>
            <Button
              onClick={() => {
                handleDelete();
              }}
              sx={{
                flexGrow: 1,
                borderRadius: "26px",
                color: "#072D26",
                fontWeight: "bold",
                border: "2px solid #C0D904",
                height: "51px",
                paddingLeft: "50px",
                paddingRight: "50px",
                "&:hover": { background: "#c0d904" },
              }}
            >
              Bestätigen
            </Button>
          </Box>
        </Box>
      </Modal>
      <Modal
        hideBackdrop
        open={errorOpened}
        onClose={() => {
          handleErrorMessage("");
        }}
      >
        <Box
          sx={{
            width: "30%",
            minWidth: "300px",
            maxWidth: "90%",
            position: "absolute",
            top: "50%",
            left: "50%",
            backgroundColor: "white",
            transform: "translate(-50%, -50%)",
            borderRadius: "16px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            padding: 4,
            textAlign: "center",
          }}
        >
          <Typography fontSize={20} fontWeight={"bold"}>
            {`Fehler beim Status setzen`}
          </Typography>
          <Typography fontSize={16} color="error">
            {errorMessage?.split(",").map((error, index) => (
              <span key={index}>
                {error}
                {index !== errorMessage?.split(",").length - 1 && <br />}
              </span>
            ))}
          </Typography>
          <Button
            onClick={() => {
              handleErrorMessage("");
              handleClose();
            }}
            sx={{
              flexGrow: 1,
              borderRadius: "26px",
              color: "#072D26",
              fontWeight: "bold",
              border: "2px solid #C0D904",
              height: "51px",
              paddingLeft: "50px",
              paddingRight: "50px",
              "&:hover": { background: "#c0d904" },
            }}
          >
            Fortfahren
          </Button>
        </Box>
      </Modal>
    </Backdrop>
  );
};

export default BulkStatusEdit;
